/**
 * Vertical waveform from the peak pyramid (W1-15 timeline-host; DESIGN.md §4.5-4.6, ux-landscape §5 B3).
 *
 * The audio worker (W2-08) computes a min/max peak pyramid of the song (`PeakLevel`s with block sizes 64, 256,
 * 1024, 4096, 16384 samples; core/audio-dsp/types.ts). The timeline draws it with time flowing up and amplitude
 * horizontal, centred in the column:
 *   - every pixel row covers the beat range [beatAtY(y + 1), beatAtY(y)], mapped to audio seconds through the
 *     tempo map and the chart offset (`WaveformSource.audioSecAtBeat`), then to sample indices;
 *   - the coarsest pyramid level whose block is no larger than the row's sample span is read (`pickLevel`), so a row
 *     costs a handful of reads at any zoom;
 *   - the row's min/max over those blocks becomes a horizontal bar (`drawWaveform`).
 * The opacity is the view setting (`TimelineViewConfig.waveformOpacity`, View menu), applied linearly in the column
 * and in the lane backdrop alike, so 0 hides the waveform everywhere.
 *
 * Two uses: the built-in "waveform" column (`createWaveformView`, order 10, shown by the host while a source is
 * set) and the backdrop behind the note lane grid, which the lane (W2-03) paints on its static layer with the same
 * `drawWaveform`, taking the source and the opacity from the host's axis context (`axisContext(axis).waveform`,
 * `.view.waveformOpacity`).
 */

import type { PeakLevel } from '../../core/audio-dsp/types';
import type { TempoMap } from '../../core/time/types';
import type { TimeAxis, TimelineColumnView, TimelineHit } from '../types';
import { axisContext } from './axis';
import { drawBeatGrid } from './gutterColumn';
import { minMaxOf } from './lod';
import type { TimelineTheme } from './theme';

export const WAVEFORM_DEF_ID = 'waveform';
export const WAVEFORM_INSTANCE_ID = 'waveform';
/** Column placement (render/types.ts TimelineColumnDef.order: gutter 0, waveform 10, lanes 20, ...). */
export const WAVEFORM_ORDER = 10;
export const WAVEFORM_WIDTH = { min: 32, default: 72, max: 240 } as const;
/** Beat lines behind the waveform stay quieter than in the lanes (bar lines keep their full weight). */
const WAVEFORM_GRID_BEAT_ALPHA = 0.5;

/** What the timeline needs to draw a song's waveform. */
export interface WaveformSource {
  /** Pyramid levels (any order); min/max per block of `block` samples, values in -1..1. */
  readonly levels: readonly PeakLevel[];
  readonly sampleRate: number;
  /** Audio (music file) seconds at a chart beat: tempo map + chart offset. */
  audioSecAtBeat(beat: number): number;
}

/**
 * Builds a source from the tempo map and the offsets. Phira plays beat 0 at music time
 * offsetMs / 1000 + infoOffsetSec (rpe-format F§3.4), so audio seconds = chart seconds + that offset.
 */
export function createWaveformSource(opts: {
  readonly levels: readonly PeakLevel[];
  readonly sampleRate: number;
  readonly tempo: Pick<TempoMap, 'secAtBeatF64'>;
  /** META.offset / 1000 + info.yml offset, in seconds. */
  readonly offsetSec: number;
}): WaveformSource {
  const levels = [...opts.levels].sort((a, b) => a.block - b.block);
  return {
    levels,
    sampleRate: opts.sampleRate,
    audioSecAtBeat: (beat) => opts.tempo.secAtBeatF64(beat) + opts.offsetSec,
  };
}

/** Coarsest level whose block is at most `samplesPerRow` (the finest level when rows are smaller than a block). */
export function pickLevel(levels: readonly PeakLevel[], samplesPerRow: number): PeakLevel | null {
  let best: PeakLevel | null = null;
  let finest: PeakLevel | null = null;
  for (const l of levels) {
    if (l.block <= 0 || l.min.length === 0) continue;
    if (finest === null || l.block < finest.block) finest = l;
    if (l.block <= samplesPerRow && (best === null || l.block > best.block)) best = l;
  }
  return best ?? finest;
}

/** Min/max of the audio between samples s0 and s1 (s0 < s1) from one level; null outside the audio. */
export function peakRange(level: PeakLevel, s0: number, s1: number): readonly [number, number] | null {
  const a = Math.min(s0, s1);
  const b = Math.max(s0, s1);
  const i0 = Math.floor(a / level.block);
  const i1 = Math.max(i0 + 1, Math.ceil(b / level.block));
  return minMaxOf(level.min, level.max, i0, i1);
}

export interface WaveformDrawOptions {
  readonly color: string;
  /** 0..1 (waveformOpacity). */
  readonly opacity: number;
  /** Fraction of the half-width a full-scale peak reaches (default 0.92). */
  readonly gain?: number;
  /** Row height in CSS px (default 1). */
  readonly rowPx?: number;
}

/**
 * Draws the waveform of the axis' visible range into the horizontal band [x, x + width] of the column, one bar per
 * row. Rows outside the audio are left empty.
 */
export function drawWaveform(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  src: WaveformSource,
  x: number,
  width: number,
  opts: WaveformDrawOptions,
): void {
  const opacity = Math.min(1, Math.max(0, opts.opacity));
  if (opacity <= 0 || width <= 0 || axis.heightPx <= 0 || src.levels.length === 0 || !(src.sampleRate > 0)) return;
  const rowPx = Math.max(1, opts.rowPx ?? 1);
  const half = width / 2;
  const cx = x + half;
  const gain = (opts.gain ?? 0.92) * half;
  // Samples per row from the visible range (tempo changes inside the view only shift the level choice slightly).
  const [b0, b1] = axis.range;
  const secSpan = Math.abs(src.audioSecAtBeat(b1) - src.audioSecAtBeat(b0));
  const rows = Math.ceil(axis.heightPx / rowPx);
  const level = pickLevel(src.levels, (secSpan * src.sampleRate) / Math.max(1, rows));
  if (level === null) return;
  ctx.save();
  ctx.globalAlpha *= opacity;
  ctx.fillStyle = opts.color;
  let sTop = src.audioSecAtBeat(axis.beatAtY(0)) * src.sampleRate;
  for (let r = 0; r < rows; r++) {
    const yTop = r * rowPx;
    const yBottom = Math.min(axis.heightPx, yTop + rowPx);
    const sBottom = src.audioSecAtBeat(axis.beatAtY(yBottom)) * src.sampleRate;
    const pk = peakRange(level, sBottom, sTop);
    sTop = sBottom;
    if (pk === null) continue;
    const lo = Math.max(-1, Math.min(1, pk[0]));
    const hi = Math.max(-1, Math.min(1, pk[1]));
    const left = cx + lo * gain;
    const w = Math.max(0.75, (hi - lo) * gain);
    ctx.fillRect(left, yTop, w, yBottom - yTop);
  }
  ctx.restore();
}

export interface WaveformViewDeps {
  source(): WaveformSource | null;
  theme(): TimelineTheme;
  opacity(): number;
}

/**
 * The built-in waveform column: static layer only, no interaction of its own (empty hits allow marquees). Beat and
 * bar lines (the editor meter, from the host's axis context) run behind the waveform, so a transient can be read
 * against the beat it should sit on.
 */
export function createWaveformView(deps: WaveformViewDeps): TimelineColumnView & { readonly scrollCache: true } {
  const empty: TimelineHit = { kind: 'empty', cursor: 'default' };
  return {
    // Placed by beats only: the host's scroll cache may draw it for a taller strip (TimelineHost `ScrollCacheView`).
    scrollCache: true,
    layout: () => undefined,
    draw(layer, ctx, axis, widthPx) {
      if (layer !== 'static') return;
      const theme = deps.theme();
      ctx.fillStyle = theme.background;
      ctx.fillRect(0, 0, widthPx, axis.heightPx);
      const grid = axisContext(axis);
      drawBeatGrid(ctx, axis, widthPx, {
        div: 1,
        meter: grid?.meter ?? null,
        theme,
        beatAlpha: WAVEFORM_GRID_BEAT_ALPHA,
      });
      // Centre line so silence still reads as "audio here".
      ctx.fillStyle = theme.gridSub;
      ctx.fillRect(Math.floor(widthPx / 2), 0, 1, axis.heightPx);
      const src = deps.source();
      if (src === null) return;
      // The opacity setting applies as is (0 hides the waveform), exactly like the lane backdrop.
      drawWaveform(ctx, axis, src, 2, widthPx - 4, { color: theme.waveform, opacity: deps.opacity() });
    },
    hitTest: () => empty,
    dispose: () => undefined,
  };
}
